import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { PaginatedMeta, TransactionRuleType, MappingRuleAction } from '@types'
import { TransactionRule } from '../utils/types'

import { PAGE_SIZE } from '@/const/common'

import { prepareResponseError, updateStoreListItem } from '../../utils/helpers'
import { applyRule, destroyRule, disableRule, enableRule, fetchRules, fetchRuleTransactions, reorderRules, storeRule, updateRule } from './utils/helpers'

import { useLinkedDataTransactionsPivotStore } from '../transactionsPivot'
import { useRepositoriesStore } from '../../repositories/index'

export const useLinkedDataMappingRulesStore = defineStore(
  'linked-data-mapping-rules',
  () => {
    // INIT
    const repositoriesStore = useRepositoriesStore()
    const linkedDataTransactionsPivotStore =
      useLinkedDataTransactionsPivotStore()

    const list = ref<TransactionRule<MappingRuleAction[]>[]>([])
    const pagination = ref<PaginatedMeta | undefined>(undefined)
    const initFlag = ref(false)
    const loading = ref(true)
    const loadingAction = ref(false)

    let abortController = new AbortController()

    const repositoryId = computed(() => repositoriesStore.getCurrentId)

    // SETTERS

    const setList = (
      data: TransactionRule<MappingRuleAction[]>[],
      meta?: PaginatedMeta,
    ) => {
      list.value = data
      pagination.value = meta
    }

    // GETTERS

    const getList = computed(() => list.value)
    const getPagination = computed(() => pagination.value)

    // ACTIONS

    const fetch = async (
      search?: string,
      page_no = 0,
      signal?: AbortSignal
    ) => {
      try {
        loading.value = true
        const { data, meta } = await fetchRules<MappingRuleAction>(TransactionRuleType.MAPPING_TO_SMART_TRANSACTION, repositoryId.value, search, page_no, signal)
        setList(data, meta)
        initFlag.value = true
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loading.value = false
      }
    }

    const fetchTransactions = async (
      ruleData: TransactionRule<MappingRuleAction[]>['data'],
      page_no = 0,
      page_size = PAGE_SIZE,
      signal?: AbortSignal,
    ) => {
      try {
        loading.value = true
        return await fetchRuleTransactions(TransactionRuleType.MAPPING_TO_SMART_TRANSACTION, ruleData, repositoryId.value, page_no, page_size, signal)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loading.value = false
      }
    }

    const store = async (data: Partial<TransactionRule<MappingRuleAction[]>>) => {
      loadingAction.value = true
      try {
        const createdRule = await storeRule<MappingRuleAction>(
          TransactionRuleType.MAPPING_TO_SMART_TRANSACTION,
          data,
          repositoryId.value,
        )
        if (data.apply_to_existing_transactions) {
          linkedDataTransactionsPivotStore.clear()
        }
        return createdRule
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const update = async (id: string, data: TransactionRule<MappingRuleAction[]>) => {
      loadingAction.value = true
      try {
        const result = await updateRule<MappingRuleAction>(id, data)
        result && updateStoreListItem(list.value, result)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const reorder = async (id: string, newIndex: number, oldIndex: number) => {
      return await reorderRules(id, repositoryId.value, newIndex, oldIndex, list.value)
    }

    const destroy = async (id: string) => {
      loadingAction.value = true
      try {
        await destroyRule(id, repositoryId.value)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const apply = async (ruleData: TransactionRule<MappingRuleAction[]>['data']) => {
      loadingAction.value = true
      try {
        const result = await applyRule<MappingRuleAction>(ruleData, repositoryId.value)
        return result
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const enable = async (id: string) => {
      loadingAction.value = true
      try {
        const result = await enableRule(id, repositoryId.value)
        updateStoreListItem(list.value, result)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const disable = async (id: string) => {
      loadingAction.value = true
      try {
        const result = await disableRule(id, repositoryId.value)
        updateStoreListItem(list.value, result)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      loading.value = true
      loadingAction.value = false
      initFlag.value = false

      setList([], undefined)
    }

    return {
      initFlag,
      loading,
      loadingAction,

      getList,
      getPagination,

      fetch,
      fetchTransactions,
      store,
      update,
      reorder,
      destroy,
      apply,
      enable,
      disable,

      cancel,
      clear,
    }
  },
)
